import { AlertStatus } from '@repo/shared/dtos/alert'
import { CaseWorkerRole, CaseWorkerStatus } from '@repo/shared/dtos/caseWorker'
import { PromptTemplateType } from '@repo/shared/dtos/organization'

import { apiAdmin } from '@/external/api/chunks/api-admin'
import { apiAlerts } from '@/external/api/chunks/api-alerts'
import { apiAuth } from '@/external/api/chunks/api-auth'
import { apiBeneficiaries } from '@/external/api/chunks/api-beneficiaries'
import { apiCaseWorkers } from '@/external/api/chunks/api-case-workers'
import { apiChatSessions } from '@/external/api/chunks/api-chat-sessions'
import { apiDocuments } from '@/external/api/chunks/api-documents'
import { apiMe } from '@/external/api/chunks/api-me'
import { apiOrganizations } from '@/external/api/chunks/api-organizations'
import { apiSudo } from '@/external/api/chunks/api-sudo'
import { apiUsers } from '@/external/api/chunks/api-users'
import { api } from '@/mocks/api'
import { IApi as IAltApi } from '@/spi/api'
import { createStore, RootState } from '@/store/index.ts'
import { initialState } from '@/store/root'
import { IAccessTokenManager, IApi } from '@/store/spi/api'
import { ISudoApi } from '@/store/spi/sudo'
import { ITracker } from '@/store/spi/tracker.ts'
import { CaseWorker, Organization } from '@/store/user'

import { mockBuilders } from './mockBuilders'

export type CustomStoreConfig = {
  accessTokenManager?: MockedIAccessTokenManager
  api?: MockedIApi
  altApi?: IAltApi
  initialState?: Partial<RootState>
  sudoApi?: ISudoApi
  tracker?: ITracker
}

class MockedTracker implements ITracker {
  identify(_: { caseWorker: CaseWorker; organization: Organization }): void {}
  track(_: { event: string }): void {}
}

export const buildMockedStore = (
  customStoreConfig?: CustomStoreConfig,
): ReturnType<typeof createStore> => {
  return createStore({
    accessTokenManager:
      (customStoreConfig?.accessTokenManager as unknown as IAccessTokenManager | undefined) ||
      (getMockedApi() as unknown as IAccessTokenManager),
    sudoApi:
      customStoreConfig?.sudoApi ||
      apiSudo({
        organizations: apiOrganizations().sudo,
        users: apiUsers().sudo,
      })(),
    api:
      (customStoreConfig?.api as unknown as IApi | undefined) ||
      (getMockedApi() as unknown as IApi),
    altApi: getMockedApi().altApi,
    socket: {
      emit: () => {},
      listen: () => {},
    },
    staticSocket: {
      emit: () => {},
      listen: () => {},
    },
    tracker: customStoreConfig?.tracker || new MockedTracker(),
    initialState: { ...initialState, ...customStoreConfig?.initialState },
  })
}

const reportTemplate = mockBuilders.reportTemplate.build()
const reportTemplates = [reportTemplate, ...mockBuilders.reportTemplate.buildX(2)]
const alert = mockBuilders.alert.build()
const alerts = [
  alert,
  ...mockBuilders.alert.buildMany([
    {
      id: 'alert-unread',
      status: AlertStatus.UNREAD,
    },
    {
      id: 'alert-read',
      status: AlertStatus.READ,
    },
    {
      id: 'alert-archived',
      status: AlertStatus.ARCHIVED,
    },
  ]),
]

const beneficiary = mockBuilders.beneficiary.build()
const beneficiaries = [beneficiary, ...mockBuilders.beneficiary.buildX(2)]

const caseWorker = mockBuilders.caseWorker.build()
const caseWorkers = [
  caseWorker,
  ...mockBuilders.caseWorker.buildMany([
    {
      id: 'cw-admin-enabled',
      role: CaseWorkerRole.Admin,
      status: CaseWorkerStatus.Enabled,
    },
    {
      id: 'cw-admin-disabled',
      role: CaseWorkerRole.Admin,
      status: CaseWorkerStatus.Disabled,
    },
    {
      id: 'cw-caseworker-enabled',
      role: CaseWorkerRole.CaseWorker,
      status: CaseWorkerStatus.Enabled,
    },
    {
      id: 'cw-caseworker-disabled',
      role: CaseWorkerRole.CaseWorker,
      status: CaseWorkerStatus.Disabled,
    },
  ]),
]

const user = mockBuilders.user.build()
const users = [user, ...mockBuilders.user.buildX(2)]

const sudoUser = mockBuilders.sudoUserBuilder.build()
const sudoUsers = [sudoUser, ...mockBuilders.sudoUserBuilder.buildX(2)]

const organization = mockBuilders.organization.build()
const organizations = [
  organization,
  ...mockBuilders.organization.buildMany([
    {
      id: 'org2',
    },
    {
      id: 'demo1',
      // @ts-expect-error for testing purposes
      featureFlags: ['demo'],
    },
    {
      id: 'demo2',
      // @ts-expect-error for testing purposes
      featureFlags: ['demo'],
    },
  ]),
]

const sudoOrganization = mockBuilders.sudoOrganizationBuilder.build()
const sudoOrganizations = [sudoOrganization, ...mockBuilders.sudoOrganizationBuilder.buildX(2)]

const task = mockBuilders.task.build()
const tasks = [task, ...mockBuilders.task.buildX(2)]

const resource = mockBuilders.resource.build()
const resources = [resource, ...mockBuilders.resource.buildX(2)]

const note = mockBuilders.note.build()
const notes = [note, ...mockBuilders.note.buildX(2)]

const chatMessage = mockBuilders.chatMessage.build()
const chatMessages = [chatMessage, ...mockBuilders.chatMessage.buildX(2)]

const chatSession = mockBuilders.chatSession.build()
const chatSessions = [chatSession, ...mockBuilders.chatSession.buildX(2)]

const feedback = mockBuilders.feedback.build()
const feedbacks = [feedback, ...mockBuilders.feedback.buildX(2)]

// Use the mock builders to create the mock data
export const mockedData = {
  alert,
  alerts,
  beneficiaries,
  beneficiary,
  caseWorker,
  caseWorkers,
  chatMessage,
  chatMessages,
  chatSession,
  chatSessions,
  feedback,
  feedbacks,
  note,
  notes,
  organization,
  organizations,
  promptTemplates: mockBuilders.promptTemplate.buildMany([
    {
      id: 'promptTemplate-1',
      promptTemplateType: PromptTemplateType.CHAT_WELCOME,
      promptText: 'promptText-promptTemplate-1',
    },
    {
      id: 'promptTemplate-2',
      promptTemplateType: PromptTemplateType.CASEWORKER_DASHBOARD_SUGGEST,
      promptText: 'promptText-promptTemplate-2',
    },
  ]),
  reportTemplate,
  reportTemplates,
  resource,
  resources,
  task,
  tasks,
  user,
  users,
  sudo: {
    organization: sudoOrganization,
    organizations: sudoOrganizations,
    user: sudoUser,
    users: sudoUsers,
  },
}

type MockedIApi = Omit<IApi, 'auth' | 'users'> & {
  auth: Omit<IApi['auth'], 'login'> & { accessToken: string }
} & { altApi: IAltApi }
type MockedIAccessTokenManager = Omit<IAccessTokenManager, 'set'>
export type GetMockedApi = MockedIApi & MockedIAccessTokenManager

export const getMockedApi = (): GetMockedApi => ({
  get() {
    return 'my-token'
  },
  clear() {},

  // TODO: it shouldn't be used. It's only for compatibility with the legacy code.
  altApi: api,

  demo: {
    get(): ReturnType<IApi['demo']['get']> {
      // Filter organizations that have the demo field
      const demoOrganizations = mockedData.organizations.filter((org) =>
        // @ts-expect-error for testing purposes
        org.featureFlags.some((flag) => flag === 'demo'),
      )
      return Promise.resolve({
        organizations: demoOrganizations,
      })
    },
    reset(_p: { organizationId: string }): ReturnType<IApi['demo']['reset']> {
      return Promise.resolve({
        success: true,
      })
    },
  },

  //
  // ADMIN
  //
  admin: apiAdmin(),

  //
  // APP
  //
  alerts: apiAlerts().app,
  auth: {
    accessToken: '',
    updateAccessToken({ accessToken }: { accessToken: string }) {
      this.accessToken = accessToken
    },
    ...apiAuth().app,
  },
  beneficiaries: apiBeneficiaries().app,
  caseWorkers: apiCaseWorkers().app,
  chatSessions: apiChatSessions().app,
  documents: apiDocuments().app,
  me: apiMe().app,
  organizations: apiOrganizations().app,
})
